import { useMemo, useState } from "react";
import { Link } from "react-router";
import { CheckCircle2 } from "lucide-react";
import { useCreatePrivilegeMutation, useGetPrivilegeCatalogQuery } from "../store";
import {
  ApiErrorText,
  apiCaughtError,
  extractLogMessage,
  isUnexpectedErrorMessage,
} from "../components/ApiErrorText";

const EMPTY_FORM = {
  privilegeName: "",
  privilegeDescription: "",
  componentPage: "",
};

function formatFieldErrors(err) {
  const errors = err?.data?.errors;
  if (!errors || typeof errors !== "object") {
    return {};
  }
  return Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key, String(value ?? "").trim()]),
  );
}

function formatSubmitError(err) {
  const fieldErrors = formatFieldErrors(err);
  const firstFieldError = Object.values(fieldErrors).find(Boolean);
  if (firstFieldError) {
    return firstFieldError;
  }
  if (err?.status === 403) {
    return "You do not have permission to create privileges.";
  }
  const detail = err?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (typeof err?.error === "string" && err.error.trim()) {
    return err.error.trim();
  }
  if (err?.status === "FETCH_ERROR") {
    return "Network error. Check that the API server is running.";
  }
  if (typeof err?.status === "number") {
    return `Unable to create privilege (HTTP ${err.status}).`;
  }
  return "Unable to create privilege.";
}

function CreateNewPrivilege() {
  const {
    data: privilegeCatalog = [],
    isLoading: catalogLoading,
    isError: catalogError,
    error: catalogLoadError,
    refetch: refetchCatalog,
  } = useGetPrivilegeCatalogQuery();
  const [createPrivilege, { isLoading: isSaving }] = useCreatePrivilegeMutation();

  const [selectedPrivilegeId, setSelectedPrivilegeId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);

  const privileges = useMemo(
    () => (Array.isArray(privilegeCatalog) ? privilegeCatalog : []),
    [privilegeCatalog],
  );

  const selectedPrivilege = useMemo(() => {
    if (!selectedPrivilegeId) {
      return null;
    }
    const id = Number.parseInt(selectedPrivilegeId, 10);
    if (!Number.isFinite(id)) {
      return null;
    }
    return privileges.find((privilege) => privilege.privilegeId === id) ?? null;
  }, [privileges, selectedPrivilegeId]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
    setSubmitError("");
    setSuccess(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setFieldErrors({});
    setSuccess(null);

    const privilegeName = String(form.privilegeName ?? "").trim().toUpperCase();
    const localErrors = {};

    if (!privilegeName) {
      localErrors.privilegeName = "Privilege name is required.";
    } else if (!/^[A-Z][A-Z0-9_]*$/.test(privilegeName)) {
      localErrors.privilegeName =
        "Use uppercase letters, digits, and underscores (e.g. VIEW_EXAMPLE_PAGE).";
    }

    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      return;
    }

    try {
      const result = await createPrivilege({
        privilegeName,
        privilegeDescription: String(form.privilegeDescription ?? "").trim() || undefined,
        componentPage: String(form.componentPage ?? "").trim() || undefined,
      }).unwrap();
      setSuccess(result);
      setForm(EMPTY_FORM);
      if (result?.privilegeId != null) {
        setSelectedPrivilegeId(String(result.privilegeId));
      }
    } catch (err) {
      const apiFieldErrors = formatFieldErrors(err);
      if (Object.keys(apiFieldErrors).length > 0) {
        setFieldErrors(apiFieldErrors);
      }
      setSubmitError(apiCaughtError(err, formatSubmitError));
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Create New Privilege
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Add a billing privilege to the catalog
        </p>
      </div>

      {success ? (
        <div className="mb-6 rounded border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 px-4 py-3 text-sm text-green-900 dark:text-green-100">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Privilege created</p>
              <p className="mt-1">
                <span className="font-mono">{success.privilegeName}</span> was added to the privilege
                catalog.
              </p>
              <Link
                to="/role-privileges"
                className="mt-2 inline-block text-green-800 dark:text-green-200 underline hover:no-underline"
              >
                View assigned role privileges
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      <div className="max-w-2xl mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        <div className="p-6 space-y-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
              Existing Privileges
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select a privilege to view its details
            </p>
          </div>

          {catalogError ? (
            <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              <p className="font-medium">Could not load privileges</p>
              <ApiErrorText
                error={
                  isUnexpectedErrorMessage(catalogLoadError?.data?.detail)
                    ? {
                        message: "Unexpected error",
                        logMessage: extractLogMessage(catalogLoadError),
                      }
                    : catalogLoadError
                }
                className="mt-1 block text-red-800 dark:text-red-200"
              />
              <button
                type="button"
                onClick={() => refetchCatalog()}
                className="app-btn-gloss mt-2 px-3 py-1.5 text-sm"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <div>
                <label
                  htmlFor="existing-privilege-select"
                  className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
                >
                  Privilege
                </label>
                <select
                  id="existing-privilege-select"
                  value={selectedPrivilegeId}
                  onChange={(e) => setSelectedPrivilegeId(e.target.value)}
                  disabled={catalogLoading || privileges.length === 0}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
                >
                  <option value="">
                    {catalogLoading
                      ? "Loading privileges…"
                      : privileges.length === 0
                        ? "No privileges found"
                        : "Select a privilege"}
                  </option>
                  {privileges.map((privilege) => (
                    <option key={privilege.privilegeId ?? privilege.privilegeName} value={privilege.privilegeId}>
                      {privilege.privilegeName || `Privilege #${privilege.privilegeId}`}
                    </option>
                  ))}
                </select>
              </div>

              {selectedPrivilege ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 space-y-3">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Privilege name
                    </div>
                    <div className="mt-1 text-sm font-mono font-medium text-gray-900 dark:text-gray-100">
                      {selectedPrivilege.privilegeName || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Privilege ID
                    </div>
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100 tabular-nums">
                      {selectedPrivilege.privilegeId ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Description
                    </div>
                    <div className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {selectedPrivilege.privilegeDescription || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Component page
                    </div>
                    <div className="mt-1 text-sm font-mono text-blue-600 dark:text-blue-400">
                      {selectedPrivilege.componentPage || "—"}
                    </div>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-sm">
        <div className="px-6 pt-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">
            Create Privilege
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Add a new billing privilege to the catalog
          </p>
        </div>
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
          <div>
            <label
              htmlFor="create-privilege-name"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
            >
              Privilege name
            </label>
            <input
              id="create-privilege-name"
              type="text"
              value={form.privilegeName}
              onChange={(e) => updateField("privilegeName", e.target.value.toUpperCase())}
              disabled={isSaving}
              placeholder="VIEW_EXAMPLE_PAGE"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Uppercase identifier used in JWT claims and route guards
            </p>
            {fieldErrors.privilegeName ? (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.privilegeName}</p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="create-privilege-description"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
            >
              Description
            </label>
            <textarea
              id="create-privilege-description"
              value={form.privilegeDescription}
              onChange={(e) => updateField("privilegeDescription", e.target.value)}
              disabled={isSaving}
              rows={3}
              placeholder="Describe what this privilege allows"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            />
            {fieldErrors.privilegeDescription ? (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {fieldErrors.privilegeDescription}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="create-privilege-component-page"
              className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-1"
            >
              Component page
            </label>
            <input
              id="create-privilege-component-page"
              type="text"
              value={form.componentPage}
              onChange={(e) => updateField("componentPage", e.target.value)}
              disabled={isSaving}
              placeholder="/example-page"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Optional frontend route path protected by this privilege
            </p>
            {fieldErrors.componentPage ? (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.componentPage}</p>
            ) : null}
          </div>

          {submitError ? (
            <div className="rounded border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/40 px-4 py-3 text-sm text-red-800 dark:text-red-200">
              <ApiErrorText error={submitError} />
            </div>
          ) : null}

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={isSaving}
              className="app-btn-gloss disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Creating…" : "Create privilege"}
            </button>
            <Link
              to="/role-privileges"
              className="app-btn-gloss-cancel"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export { CreateNewPrivilege };
